import React, { useMemo, useState } from 'react'
import moment from 'moment'
import ListTable from '../ListTable'
import SegmentedSwitch from '../SegmentedSwitch'

// Channel Properties (from the export tool) is a set of letters: B - Base, F - Fluid, P - Proppant group;
// C - current value, D - design value. Other letters are not used for grouping
const GROUPS = [
    { key: 'base', label: 'Base', letter: 'B' },
    { key: 'fluid', label: 'Fluid', letter: 'F' },
    { key: 'proppant', label: 'Proppant', letter: 'P' },
    { key: 'all', label: 'All' },
]

const MODES = [
    { key: 'current', label: 'Current', letters: ['C'] },
    { key: 'design', label: 'Design', letters: ['D'] },
    { key: 'currentAndDesign', label: 'Current and design', letters: ['C', 'D'] },
]

// Shown as the Step column already
const STEP_NUMBER_CHANNEL = 'StepNumber'

const hasLetter = (channel, letter) => (channel.properties || '').includes(letter)

const isCurrentChannel = (channel) => hasLetter(channel, 'C') && !hasLetter(channel, 'D')

// Only numeric values count: the export tool fills date (StepStartTime) and text values ahead for all steps
const hasValue = (item) => item?.value != null && item.value !== 0

const formatCellValue = (channel, item) => {
    if (!item) return ''
    switch (channel.dataType) {
        case 't_DateTime':
            return item.timeValue != null ? moment(item.timeValue).format('YYYY-MM-DD HH:mm:ss') : ''
        case 't_String':
            return item.textValue ?? ''
        default:
            return item.value ?? ''
    }
}

const PumpingSchedule = ({ data }) => {

    const [group, setGroup] = useState('base')
    const [mode, setMode] = useState('current')

    const channels = useMemo(() => data?.channels || [], [data])
    const rows = useMemo(() => data?.data || [], [data])

    const visibleChannels = useMemo(() => {
        const groupDef = GROUPS.find(g => g.key === group)
        if (!groupDef?.letter) return channels
        const modeLetters = MODES.find(m => m.key === mode).letters
        return channels
            .filter(channel => channel.name !== STEP_NUMBER_CHANNEL
                && hasLetter(channel, groupDef.letter)
                && modeLetters.some(letter => hasLetter(channel, letter)))
            .sort((a, b) => a.dmsId - b.dmsId)
    }, [channels, group, mode])

    // Step the latest current (not design) data arrived for
    const currentStep = useMemo(() => {
        const currentChannelIds = new Set(channels.filter(isCurrentChannel).map(channel => channel.id))
        let latest = null
        rows.forEach(item => {
            if (!currentChannelIds.has(item.pumpingScheduleChannelId) || !hasValue(item)) return
            if (!latest || item.timestamp > latest.timestamp || (item.timestamp === latest.timestamp && item.step > latest.step)) {
                latest = item
            }
        })
        return latest ? latest.step : null
    }, [rows, channels])

    const tableColumns = useMemo(() => {
        return [
            {
                Header: 'Step',
                accessor: 'step',
            },
            ...visibleChannels.map(channel => ({
                Header: channel.displayUnit ? `${channel.name} (${channel.displayUnit})` : channel.name,
                accessor: `channel_${channel.id}`,
            })),
        ]
    }, [visibleChannels])

    const tableData = useMemo(() => {
        const steps = [...new Set(rows.map(item => item.step))].sort((a, b) => a - b)
        return steps.map(step => {
            const row = { step }
            visibleChannels.forEach(channel => {
                const item = rows.find(r => r.step === step && r.pumpingScheduleChannelId === channel.id)
                row[`channel_${channel.id}`] = formatCellValue(channel, item)
            })
            return row
        })
    }, [rows, visibleChannels])

    const isAll = group === 'all'

    return (
        <div>
            <ListTable
                // Remount per tab: hidden columns from the column picker apply to All only
                key={isAll ? 'all' : `${group}-${mode}`}
                emptyDataText={(
                    <span>
                        <span>No pumping schedule data found.</span>
                    </span>
                )}
                columns={tableColumns}
                enableSearch={false}
                enableColumnFilters={true}
                columnVisibilityMenu={isAll}
                columnVisibilityStorageKey={isAll ? 'pumpingScheduleColumns' : undefined}
                data={tableData}
                tableClass="bordered"
                getRowClassName={(row) => row.step === currentStep ? 'pumpingScheduleCurrentRow' : undefined}
                toolbarStart={(
                    <div className="pumpingScheduleSwitches">
                        <SegmentedSwitch options={GROUPS} value={group} onChange={setGroup} />
                        {/* Mobile only: forces the mode switch + filters button onto their own row */}
                        <span className="pumpingScheduleSwitchesBreak" aria-hidden="true" />
                        {!isAll && <SegmentedSwitch options={MODES} value={mode} onChange={setMode} />}
                    </div>
                )}
            />
        </div>
    )
}

export default PumpingSchedule
